import RAPIER from '@dimforge/rapier3d-compat';
import type { RigidBody, World } from '@dimforge/rapier3d-compat';
import * as THREE from 'three';

import { BALL, HOLD } from './constants';
import { createBallBody } from '../physics/bodies';
import { createBallMaterial } from '../scene/materials';

/**
 * The metallic ball.
 *
 * The ball is a plain dynamic body in WORLD space - it is never parented to the
 * maze body, because a child would orbit with the board instead of staying put
 * while the board spins underneath it.
 *
 * `isSpawned` is the single guard that prevents the game from ever creating a
 * second ball.
 *
 * ---------------------------------------------------------------------------
 * HELD, THEN RELEASED
 * ---------------------------------------------------------------------------
 * A round starts with the ball HELD: it exists, it is visible, and it hangs
 * above the board while the 3-2-1 countdown runs and the player aims. Holding is
 * implemented by switching gravity off and zeroing the velocity every step,
 * rather than by not creating the ball until later, so the player can see where
 * the ball is going to come from while there is still time to do something about
 * it.
 *
 * The ball is created AT the hold position, not at the entry point, so there is
 * no teleport at the start of a round - the drop the player watches is the drop
 * that actually happens.
 */
export class Ball {
  /**
   * The ball's visual: the loaded `ball.glb`, or the procedural sphere.
   * Held as an Object3D so either source is driven identically.
   */
  private readonly mesh: THREE.Object3D;
  private body: RigidBody | null = null;
  /** True while gravity is switched off and the ball is waiting to be released. */
  private held = false;
  /** True when the production model could not be used. */
  readonly usedFallback: boolean;

  constructor(
    private readonly world: World,
    scene: THREE.Scene,
    visual?: THREE.Object3D,
  ) {
    this.usedFallback = !visual;
    this.mesh =
      visual ??
      new THREE.Mesh(new THREE.SphereGeometry(BALL.radius, 40, 28), createBallMaterial());
    this.mesh.name = 'ball';
    this.mesh.visible = false;
    scene.add(this.mesh);
  }

  /** The visual root, exposed so tests can measure the loaded model. */
  get visual(): THREE.Object3D {
    return this.mesh;
  }

  get isSpawned(): boolean {
    return this.body !== null;
  }

  /** True while the countdown is holding the ball. */
  get isHeld(): boolean {
    return this.held;
  }

  get rigidBody(): RigidBody | null {
    return this.body;
  }

  /** Create the ball if it does not already exist, held above the entry point. */
  spawn(): void {
    if (this.body) return;

    this.body = createBallBody(this.world, HOLD.x, HOLD.y, BALL.z);

    this.world.createCollider(
      RAPIER.ColliderDesc.ball(BALL.radius)
        .setDensity(BALL.density)
        .setFriction(BALL.friction)
        .setRestitution(BALL.restitution)
        .setContactSkin(BALL.contactSkin),
      this.body,
    );

    this.mesh.visible = true;
    this.hold();
    this.syncVisuals();
  }

  /** Remove the ball from the simulation entirely. */
  despawn(): void {
    if (!this.body) return;
    // Removing the body also removes every collider attached to it.
    this.world.removeRigidBody(this.body);
    this.body = null;
    this.held = false;
    this.mesh.visible = false;
  }

  /** Despawn and spawn again, held above the entry point. */
  reset(): void {
    this.despawn();
    this.spawn();
  }

  /**
   * Freeze the ball where it is: no gravity, no velocity.
   *
   * Idempotent, and called every fixed step while the countdown runs. Zeroing
   * the velocity each step is what makes this a genuine hold rather than a
   * one-off nudge - the board's colliders are moving underneath, and a ball
   * merely given zero gravity could still be picked up and carried by one.
   */
  hold(): void {
    if (!this.body) return;
    this.body.setGravityScale(0, true);
    this.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
    this.held = true;
  }

  /** Hand the ball back to gravity. */
  release(): void {
    if (!this.body) return;
    if (!this.held) return;
    this.body.setGravityScale(1, true);
    this.held = false;
  }

  /**
   * Move the live ball to an explicit world position and stop it dead.
   *
   * Used by the verification harness to place the ball in a chosen pocket, or
   * off the board entirely, without first having to steer it there. Velocity is
   * zeroed so the placed ball begins at rest rather than inheriting whatever it
   * had before, and the ball is RELEASED: placing a ball in the world means it
   * should behave like a ball in the world, and a test that teleports a ball and
   * then wonders why it hangs in mid-air is a test measuring the wrong thing.
   */
  teleport(x: number, y: number, z: number): void {
    if (!this.body) return;
    this.body.setTranslation({ x, y, z }, true);
    this.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
    this.release();
    this.syncVisuals();
  }

  /** Copy the simulated transform onto the mesh. */
  syncVisuals(): void {
    if (!this.body) return;

    const translation = this.body.translation();
    this.mesh.position.set(translation.x, translation.y, translation.z);

    const rotation = this.body.rotation();
    this.mesh.quaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
  }

  /** Radial distance from the board centre - the scoring axis. */
  get radiusFromCenter(): number {
    if (!this.body) return 0;
    const t = this.body.translation();
    return Math.hypot(t.x, t.y);
  }

  /** Linear speed in m/s. */
  get speed(): number {
    if (!this.body) return 0;
    const v = this.body.linvel();
    return Math.hypot(v.x, v.y, v.z);
  }

  /**
   * True while Rapier has the ball asleep.
   *
   * A sleeping body is skipped by the solver and by gravity, so it will sit
   * wherever it dozed off. The ball must never be allowed to sleep: it is
   * supposed to keep rolling until it reaches the bottom of its channel.
   */
  get isSleeping(): boolean {
    return this.body?.isSleeping() ?? false;
  }

  get position(): { x: number; y: number; z: number } {
    if (!this.body) return { x: 0, y: 0, z: 0 };
    const t = this.body.translation();
    return { x: t.x, y: t.y, z: t.z };
  }
}
